// Copyright 2026, Imprivata, Inc.  All rights reserved.

import type { AppConfig, AppConfigUpdateRequest, AppIntegration } from '@api/types';
import { useSession } from '@common/authorization/context/Session';
import { useErrorContext } from '@errorHandler/context/ErrorContext';
import { type ApiError, AppError } from '@errorHandler/errors';
import { Button, Modal } from '@imprivata-cloud/components';
import { Form, message } from 'antd';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import HyperdriveConfigForm, {
  type HyperdriveFormValues,
} from '../components/HyperdriveConfigForm';
import SamlMetadataBlock from '../components/SamlMetadataBlock';
import { useAppConfigMutation, useAppConfigQuery } from '../hooks/useAppConfig';
import './HyperdriveModal.less';

const APP_ID = 'epic-hyperdrive';
const TRANSLATION_ROOT = 'apps.app-modal';

// Verbatim port of the legacy `isAppConfigured` helper from saas-admin-ui's
// AppModal (REQ-007 / AC-005). Drives Add vs. Edit titling and action label.
export const isAppConfigured = (
  ai: AppIntegration | undefined,
  ac: AppConfig | undefined,
): boolean => ai?.idpIssuer != null && ac?.appUsernameAdMatch != null;

interface HyperdriveModalProps {
  appIntegration: AppIntegration;
  onClose: () => void;
}

const HyperdriveModal: React.FC<HyperdriveModalProps> = ({ appIntegration, onClose }) => {
  const { t } = useTranslation();
  const { tenantId } = useSession();
  const [form] = Form.useForm<HyperdriveFormValues>();
  const [messageApi, contextHolder] = message.useMessage();
  const { emitError, clearErrorNotifications } = useErrorContext();
  const { data, isLoading, error } = useAppConfigQuery(APP_ID);
  const { mutate, isPending } = useAppConfigMutation(APP_ID);

  const appConfig = data?.appConfig;
  const configured = isAppConfigured(appIntegration, appConfig);
  const titleKey = configured ? 'title-edit' : 'title-add';
  const actionKey = configured ? 'action-save' : 'action-add';

  // Surface a find-failure exactly once per error (REQ-014 / AC-012).
  useEffect(() => {
    if (error) {
      emitError(new AppError(error));
    }
  }, [error, emitError]);

  // REQ-009 / AC-017 legacy parity: pre-fill the mapping dropdown with
  // `false` when no value has been saved yet, matching saas-admin-ui's
  // DEFAULT_EPIC_USERNAME_MATCHES_AD behavior.
  // REQ-006 / AC-019: seed the lock dropdowns from saved values; leave
  // each sub-key undefined when not saved so the placeholder shows.
  const initialValues: HyperdriveFormValues = {
    appUsernameAdMatch: appConfig?.appUsernameAdMatch ?? false,
    interconnectUrl: appConfig?.interconnectBaseUrls?.[0] ?? '',
    epicAppLockConfig: {
      sharedWorkstation: appConfig?.epicAppLockConfig?.sharedWorkstation,
      privateWorkstation: appConfig?.epicAppLockConfig?.privateWorkstation,
    },
  };

  const onFinish = (values: HyperdriveFormValues) => {
    clearErrorNotifications();
    const trimmed = values.interconnectUrl?.trim();
    const interconnectBaseUrls = trimmed ? [trimmed] : [];
    // REQ-006 / AC-020: the wire payload always carries
    // `epicAppLockConfig`, but only sub-keys that the admin actually
    // selected — `undefined` slots are stripped so an untouched form
    // submits `{}` rather than `{ sharedWorkstation: undefined, … }`.
    const rawLock = values.epicAppLockConfig ?? {};
    const epicAppLockConfig = Object.fromEntries(
      Object.entries(rawLock).filter(([, v]) => v !== undefined),
    );
    const req: AppConfigUpdateRequest = {
      applicationId: APP_ID,
      appConfig: {
        samlEnabled: true,
        captureMaxRetries: appConfig?.captureMaxRetries ?? 3,
        appUsernameAdMatch: values.appUsernameAdMatch,
        interconnectBaseUrls,
        epicAppLockConfig,
      },
    };
    mutate(req, {
      onSuccess: () => {
        messageApi.success({ content: t(`${TRANSLATION_ROOT}.save-success`) });
        onClose();
      },
      onError: (e: ApiError) => {
        emitError(new AppError(e));
      },
    });
  };

  // AC-024 (REQ-009 / REQ-018 / REQ-019): SAML metadata is injected
  // into the form as a slot so the form owns vertical placement —
  // between the mapping Select and the Interconnect URL InputBox.
  // Sibling-rendering put SAML above the form body in the prior build.
  const samlMetadata = (
    <SamlMetadataBlock
      idpIssuer={appIntegration.idpIssuer}
      redirectUri={data?.redirectUri ?? ''}
      idpCertificate={appIntegration.idpCertificates[0]?.idpCertificate ?? ''}
      tenantId={tenantId ?? ''}
    />
  );

  return (
    <Modal
      open
      // REQ-020 / AC-021: scope the modal-only stylesheet (footer
      // alignment, SAML row layout, section-title rhythm) to this
      // modal. Matches the `.user-modal` pattern in `UserModal.tsx`.
      className="hyperdrive-modal"
      title={t(`${TRANSLATION_ROOT}.${titleKey}`, {
        appDisplayName: appIntegration.appDisplayName,
      })}
      width={650}
      onCancel={onClose}
      footer={null}
      maskClosable={false}
      destroyOnHidden
    >
      {contextHolder}
      {isLoading ? (
        <div>{t(`${TRANSLATION_ROOT}.loading`)}</div>
      ) : (
        <Form form={form} layout="vertical" initialValues={initialValues} onFinish={onFinish}>
          <HyperdriveConfigForm
            form={form}
            appDisplayName={appIntegration.appDisplayName}
            samlMetadata={samlMetadata}
          />
          <div className="form-footer-right">
            <Button onClick={onClose} label={t(`${TRANSLATION_ROOT}.action-cancel`)} />
            <Button
              type="primary"
              htmlType="submit"
              label={t(`${TRANSLATION_ROOT}.${actionKey}`)}
              disabled={isPending}
            />
          </div>
        </Form>
      )}
    </Modal>
  );
};

export default HyperdriveModal;
